import { RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import collaborationService from '../../services/collaborationService';
import influencerService from '../../services/influencerService';
import LoadingSpinner from '../ui/LoadingSpinner';
import CollaborationModal from './CollaborationModal';

const COLLAB_STATUS_LABELS = {
  shortlisted: { label: 'Shortlisted', bg: 'bg-muted', text: 'text-muted-foreground' },
  outreach_pending: {
    label: 'Outreach Pending',
    bg: 'bg-yellow-100 dark:bg-yellow-900/40',
    text: 'text-yellow-700 dark:text-yellow-300',
  },
  reached_out: {
    label: 'Reached Out',
    bg: 'bg-blue-100 dark:bg-blue-900/40',
    text: 'text-blue-700 dark:text-blue-300',
  },
  no_response: {
    label: 'No Response',
    bg: 'bg-orange-100 dark:bg-orange-900/40',
    text: 'text-orange-700 dark:text-orange-300',
  },
  responded: { label: 'Responded', bg: 'bg-cyan-100 dark:bg-cyan-900/40', text: 'text-cyan-700 dark:text-cyan-300' },
  in_negotiation: {
    label: 'In Negotiation',
    bg: 'bg-purple-100 dark:bg-purple-900/40',
    text: 'text-purple-700 dark:text-purple-300',
  },
  deal_closed: {
    label: 'Deal Closed',
    bg: 'bg-indigo-100 dark:bg-indigo-900/40',
    text: 'text-indigo-700 dark:text-indigo-300',
  },
  brief_sent: {
    label: 'Brief Sent',
    bg: 'bg-violet-100 dark:bg-violet-900/40',
    text: 'text-violet-700 dark:text-violet-300',
  },
  content_in_progress: {
    label: 'Content In Progress',
    bg: 'bg-blue-100 dark:bg-blue-900/40',
    text: 'text-blue-800 dark:text-blue-300',
  },
  content_submitted: {
    label: 'Content Submitted',
    bg: 'bg-teal-100 dark:bg-teal-900/40',
    text: 'text-teal-700 dark:text-teal-300',
  },
  revision_requested: {
    label: 'Revision Requested',
    bg: 'bg-amber-100 dark:bg-amber-900/40',
    text: 'text-amber-700 dark:text-amber-300',
  },
  content_approved: {
    label: 'Content Approved',
    bg: 'bg-lime-100 dark:bg-lime-900/40',
    text: 'text-lime-700 dark:text-lime-300',
  },
  content_live: {
    label: 'Content Live',
    bg: 'bg-green-100 dark:bg-green-900/40',
    text: 'text-green-700 dark:text-green-300',
  },
  payment_pending: {
    label: 'Payment Pending',
    bg: 'bg-red-100 dark:bg-red-900/40',
    text: 'text-red-700 dark:text-red-300',
  },
  payment_done: {
    label: 'Payment Done',
    bg: 'bg-emerald-100 dark:bg-emerald-900/40',
    text: 'text-emerald-700 dark:text-emerald-300',
  },
  campaign_complete: {
    label: 'Campaign Complete',
    bg: 'bg-green-200 dark:bg-green-900/60',
    text: 'text-green-800 dark:text-green-300',
  },
  declined: { label: 'Declined', bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-600 dark:text-red-400' },
  ghosted: { label: 'Ghosted', bg: 'bg-muted', text: 'text-muted-foreground' },
  on_hold: {
    label: 'On Hold',
    bg: 'bg-yellow-100 dark:bg-yellow-900/40',
    text: 'text-yellow-600 dark:text-yellow-400',
  },
  blacklisted: { label: 'Blacklisted', bg: 'bg-red-200 dark:bg-red-900/60', text: 'text-red-800 dark:text-red-300' },
};

export default function PipelineTab() {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [collabs, setCollabs] = useState([]);
  const [influencerMap, setInfluencerMap] = useState({});
  const [filterStatus, setFilterStatus] = useState('');
  const [sortBy, setSortBy] = useState('updated');
  const [editingCollab, setEditingCollab] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [collabData, infData] = await Promise.all([
        collaborationService.getCollaborations(),
        influencerService.getInfluencers({ limit: 500 }).then((r) => r.items),
      ]);
      setCollabs(collabData);
      const map = {};
      infData.forEach((inf) => {
        map[inf.id] = inf;
      });
      setInfluencerMap(map);
    } catch {
      showToast('Failed to load pipeline', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = filterStatus ? collabs.filter((c) => c.status === filterStatus) : collabs;
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'updated') return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    if (sortBy === 'cycle') return b.cycle_number - a.cycle_number;
    if (sortBy === 'rate') return parseFloat(b.agreed_rate || 0) - parseFloat(a.agreed_rate || 0);
    return 0;
  });

  const overdueCount = collabs.filter((c) => c.follow_up_date && new Date(c.follow_up_date) < new Date()).length;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Pipeline</h2>
          <p className="text-muted-foreground text-sm mt-1">
            All collaborations across influencers — {collabs.length} total
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 border border-input rounded-lg hover:bg-muted/50 text-sm"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Summary pills */}
      {!loading && overdueCount > 0 && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-900 rounded-lg text-sm text-red-700 dark:text-red-300">
          <span className="font-medium">
            ⚠ {overdueCount} overdue follow-up{overdueCount > 1 ? 's' : ''}
          </span>
          <button
            onClick={() => {
              setFilterStatus('');
              setSortBy('updated');
            }}
            className="ml-2 underline text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-xs"
          >
            View all
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-card rounded-lg shadow p-4 flex flex-wrap gap-3 items-center">
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 text-sm border border-input rounded-lg focus:ring-2 focus:ring-foreground"
        >
          <option value="">All Statuses</option>
          {Object.entries(COLLAB_STATUS_LABELS).map(([value, { label }]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-2 text-sm border border-input rounded-lg focus:ring-2 focus:ring-foreground"
        >
          <option value="updated">Sort: Last Updated</option>
          <option value="cycle">Sort: Cycle #</option>
          <option value="rate">Sort: Agreed Rate</option>
        </select>
        <span className="ml-auto text-sm text-muted-foreground">
          {sorted.length} collaboration{sorted.length !== 1 ? 's' : ''}
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : sorted.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-10 text-center text-muted-foreground/70">
          No collaborations found. Start creating collaborations in influencer drawers.
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted/50">
                <tr>
                  {['Influencer', 'Status', 'Cycle', 'Rate', 'Content Due', 'Follow-up', 'Updated'].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {sorted.map((collab) => {
                  const inf = influencerMap[collab.influencer_id];
                  const cs = COLLAB_STATUS_LABELS[collab.status] || {
                    label: collab.status,
                    bg: 'bg-muted',
                    text: 'text-muted-foreground',
                  };
                  const followUpDate = collab.follow_up_date ? new Date(collab.follow_up_date) : null;
                  const contentDueDate = collab.content_due_date ? new Date(collab.content_due_date) : null;
                  const isFollowUpOverdue = followUpDate && followUpDate < new Date();
                  const isContentOverdue = contentDueDate && contentDueDate < new Date();
                  return (
                    <tr
                      key={collab.id}
                      className="hover:bg-muted/50 cursor-pointer"
                      onClick={() => inf && setEditingCollab({ collab, inf })}
                    >
                      <td className="px-4 py-3">
                        {inf ? (
                          <div>
                            <p className="text-sm font-medium text-foreground">{inf.name}</p>
                            <p className="text-xs text-muted-foreground/70">@{inf.ig_username}</p>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground/70">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${cs.bg} ${cs.text}`}>
                          {cs.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground/80">#{collab.cycle_number}</td>
                      <td className="px-4 py-3 text-sm text-foreground/80">
                        {collab.agreed_rate
                          ? `${collab.agreed_currency} ${parseFloat(collab.agreed_rate).toLocaleString()}`
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {contentDueDate ? (
                          <span
                            className={
                              isContentOverdue ? 'text-red-600 dark:text-red-400 font-medium' : 'text-foreground/80'
                            }
                          >
                            {isContentOverdue ? '⚠ ' : ''}
                            {contentDueDate.toLocaleDateString()}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {followUpDate ? (
                          <span
                            className={
                              isFollowUpOverdue ? 'text-red-600 dark:text-red-400 font-medium' : 'text-foreground/80'
                            }
                          >
                            {isFollowUpOverdue ? '⚠ ' : ''}
                            {followUpDate.toLocaleDateString()}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {new Date(collab.updated_at).toLocaleDateString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editingCollab && (
        <CollaborationModal
          influencer={editingCollab.inf}
          collaboration={editingCollab.collab}
          onClose={() => setEditingCollab(null)}
          onSave={() => {
            setEditingCollab(null);
            load();
          }}
        />
      )}
    </div>
  );
}
